// SPDX-License-Identifier: AGPL-3.0-or-later

import type {RoomAvatars} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import VoiceSceneSettings from '@app/features/voice_scene/state/VoiceSceneSettings';
import {encodeHead, ROOM_HEAD_TOPIC, ROOM_LIMITS} from '@fluxer/constants/src/RoomCharacters';
import type {Room} from 'livekit-client';
import {useEffect} from 'react';

const SEND_INTERVAL_MS = 1000 / ROOM_LIMITS.headSendHz;

// Sends where my character looks (avatars.onLocalHead: towards the pointer, or back to the front), lossy, at most
// twice a second, only for a change of ≥ 5°, with the window focused and at most 25 people in the room. The angles go
// only into the packet: never logged, stored or sent to the API. Call from an observer (shareHeadDirection).
export function useHeadDirectionSender({avatars, room}: {avatars: RoomAvatars | null; room: Room | null}): void {
	const enabled = VoiceSceneSettings.shareHeadDirection;
	useEffect(() => {
		if (!avatars || !room || !enabled) return;
		let sent: {yawDeg: number; pitchDeg: number} | null = null;
		let sentAt = Number.NEGATIVE_INFINITY;
		let pending: {yawDeg: number; pitchDeg: number} | null = null;
		let timer: ReturnType<typeof setTimeout> | null = null;
		const flush = (): void => {
			timer = null;
			const next = pending;
			pending = null;
			if (!next || !document.hasFocus()) return;
			if (room.remoteParticipants.size + 1 > ROOM_LIMITS.headMaxParticipants) return;
			if (
				sent &&
				Math.abs(next.yawDeg - sent.yawDeg) < ROOM_LIMITS.headMinDeltaDeg &&
				Math.abs(next.pitchDeg - sent.pitchDeg) < ROOM_LIMITS.headMinDeltaDeg
			)
				return;
			sent = next;
			sentAt = performance.now();
			// Lossy by design: a lost packet is replaced by the next one.
			room.localParticipant
				.publishData(encodeHead(next.yawDeg, next.pitchDeg), {
					reliable: false,
					topic: ROOM_HEAD_TOPIC,
				})
				.catch(() => {});
		};
		const off = avatars.onLocalHead((yawDeg, pitchDeg) => {
			pending = {yawDeg, pitchDeg};
			if (timer !== null) return;
			const wait = sentAt + SEND_INTERVAL_MS - performance.now();
			if (wait <= 0) flush();
			else timer = setTimeout(flush, wait);
		});
		return () => {
			off();
			if (timer !== null) clearTimeout(timer);
		};
	}, [avatars, room, enabled]);
}
